import { rctiEmailPreview } from "utils/rctiEmailPreview";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { LuCircleCheck, LuLoaderCircle, LuMail, LuX } from "react-icons/lu";
import "./rctiDetail.css";
import "./rctiDocumentJourney.css";
import RCTIEligibilityReview from "./RCTIEligibilityReview";
import RCTIAdjustmentComparison from "./RCTIAdjustmentComparison";
import RCTIDocument from "./RCTIDocument";
import { RctiSteps, rctiSteps } from "./RctiDesignSystem";
import SendInvoiceModal from "layouts/Invoices/SendInvoiceModal";
import PageSkeleton from "components/Skeleton/PageSkeleton";
import Modal from "@mui/material/Modal";
import "layouts/Invoices/invoiceFlow.css";
import "./rctiResponsive.css";

const cleanMessage = (value) =>
  String(value || "")
    .replace(/<br\s*\/?\s*>/gi, "\n")
    .replace(/<\/(p|div|li)>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .trim();

const escapeMessage = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .split(/\r?\n/)
    .map((line) => line || "&nbsp;")
    .join("<br />");

export default function RCTIIssueCommunications({
  open,
  dark = false,
  phase = "review",
  errorMessage = "",
  values = {},
  organisationName = "",
  currencyCode = "AUD",
  amountPaid = 0,
  outstandingAmount,
  appliedAdjustments = 0,
  previewItems = [],
  templatesData,
  onClose,
  onNavigateTo,
  onSubmit,
  onViewDetails,
  agreement,
  buyer,
  supplier,
  adjustmentOriginal,
  initialReviewStep = 1,
}) {
  const rootRef = useRef(null);
  const dialogRef = useRef(null);
  const issueIsBusy = phase === "issuing" || phase === "loading";
  const issueSucceeded = phase === "sent";
  const [workspaceBounds, setWorkspaceBounds] = useState(null);
  const isAdjustment = Boolean(adjustmentOriginal);
  useLayoutEffect(() => {
    if (!open) return undefined;
    const update = () => {
      const shell = rootRef.current?.closest(".invoice-flow-shell");
      const main = shell?.querySelector(".invoice-flow-main");
      if (main) {
        const rect = main.getBoundingClientRect();
        setWorkspaceBounds(
          rect.width
            ? {
                left: rect.left,
                width: rect.width,
                top: 0,
              }
            : null
        );
        return main;
      }
      return null;
    };
    const main = update();
    const observer =
      main && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(update)
        : null;
    if (main) observer?.observe(main);
    window.addEventListener("resize", update);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [open]);
  const [reviewStep, setReviewStep] = useState(1);
  const [eligibilityReady, setEligibilityReady] = useState(false);
  const [authorised, setAuthorised] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [validationError, setValidationError] = useState("");
  const [showFullPreview, setShowFullPreview] = useState(false);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  useLayoutEffect(() => {
    if (open && dialogRef.current) dialogRef.current.scrollTop = 0;
  }, [open, reviewStep, issueSucceeded]);

  const emailInitialised = useRef(false);
  useEffect(() => {
    if (!open) {
      emailInitialised.current = false;
      return;
    }
    if (!templatesData || emailInitialised.current) return;
    emailInitialised.current = true;
    setSubject(
      templatesData.emailTemplate?.templateSubject ||
        `${values.rctiNumber || "RCTI"} from ${organisationName}`
    );
    setMessage(cleanMessage(templatesData.emailTemplate?.templateMessage));
    setValidationError("");
  }, [open, templatesData, values.rctiNumber, organisationName]);

  const reviewInitialised = useRef(false);
  useEffect(() => {
    if (!open) { reviewInitialised.current = false; return; }
    if (!reviewInitialised.current) {
      reviewInitialised.current = true;
      setReviewStep(
        Number(values.emailSent) > 0 || isAdjustment
          ? 2
          : initialReviewStep === 2
          ? 2
          : 1
      );
      setEligibilityReady(false);
      setAuthorised(false);
    }
  }, [open, values.emailSent, isAdjustment, initialReviewStep]);

  useEffect(() => {
    if (!open) return undefined;
    const focused = document.activeElement;
    const frame = requestAnimationFrame(() =>
      document.querySelector(".rcti-communication-close")?.focus()
    );
    return () => {
      cancelAnimationFrame(frame);
      if (focused?.isConnected) focused.focus?.({ preventScroll: true });
    };
  }, [open]);

  if (!open) return null;

  const amount = Number(values.finalAmount || 0).toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const paid = Number(amountPaid || 0).toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const outstanding = Number(
    outstandingAmount ?? values.finalAmount ?? 0
  ).toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const adjustments = Number(appliedAdjustments || 0).toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const supplyDate =
    values.supplyDate && !Number.isNaN(new Date(values.supplyDate).getTime())
      ? new Intl.DateTimeFormat("en-AU", {
          day: "numeric",
          month: "long",
          year: "numeric",
          timeZone: "UTC",
        }).format(new Date(values.supplyDate))
      : "—";
  const supplierName =
    values.supplierCompanyName || values.supplierName || "Supplier";
  const supplierEmail = String(
    supplier?.emailAddress || supplier?.email || ""
  ).trim();
  const supplierAddress = supplierEmail
    ? `${supplierName} <${supplierEmail}>`
    : `${supplierName} <Supplier email unavailable>`;
  const templateMessage = cleanMessage(
    templatesData?.emailTemplate?.templateMessage
  );
  const preserveTemplateTokens = (displayMessage) => {
    const tokens = [
      "[RCTINumber]",
      "[CustomerName]",
      "[RCTIAmount]",
      "[RCTIDate]",
      "[SupplyDate]",
      "[DueDate]",
      "[RCTIStatus]",
    ];
    return tokens.reduce((result, token) => {
      if (!templateMessage.includes(token)) return result;
      const displayedValue = rctiEmailPreview(
        token,
        values,
        supplier,
        currencyCode
      );
      return displayedValue && displayedValue !== token
        ? result.split(displayedValue).join(token)
        : result;
    }, displayMessage);
  };
  const emailStatus = isAdjustment
    ? "Adjustment · Not issued"
    : values.issuedAt || Number(values.emailSent) > 0
    ? "Sent"
    : values.rctiID || values._id
    ? "Saved draft"
    : "Ready to create";
  const documentPreview = (
    <RCTIDocument
      values={
        isAdjustment
          ? {
              ...values,
              buyerIdentity: values.buyerIdentity || buyer,
              supplierIdentity: values.supplierIdentity || supplier,
              issuedAt: null,
              emailSent: 0,
              lifecycleStatus: "draft",
              version: Math.max(
                Number(values.version || 1),
                Number(adjustmentOriginal.version || 1) + 1
              ),
            }
          : {
              ...values,
              buyerIdentity: values.buyerIdentity || buyer,
              supplierIdentity: values.supplierIdentity || supplier,
            }
      }
      items={previewItems.length ? previewItems : values.itemsList || []}
      documents={[]}
      showAttachments={false}
      organisation={organisationName}
      rctiID={values._id || values.rctiID || "preview"}
      loadingPage={false}
      currencyCode={currencyCode}
      subtotalExcludingGst={Number(values.totalAmount || 0)}
      gstSummaryLabel={values.gstType === "Exclusive" ? "GST" : "GST (included)"}
      total={Number(values.finalAmount || 0)}
      money={(value) =>
        `${currencyCode} ${Number(value || 0).toLocaleString("en-AU", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`
      }
      dateValue={(value) =>
        value
          ? new Date(value).toLocaleDateString("en-AU", { timeZone: "UTC" })
          : "Not recorded"
      }
    />
  );

  const submit = (event, messageOverride) => {
    event.preventDefault();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(supplierEmail)) {
      setValidationError("The supplier does not have a valid email address.");
      return;
    }
    if (!subject.trim()) {
      setValidationError("Enter an email subject.");
      return;
    }
    const messageToSend = messageOverride === undefined ? message : messageOverride;
    if (!messageToSend.trim()) {
      setValidationError("Enter a message for the supplier.");
      return;
    }
    if (!authorised) {
      setValidationError("Confirm your authority to issue this RCTI.");
      return;
    }
    setValidationError("");
    onSubmit?.({
      screenType: templatesData?.screenType || "RCTI",
      selectedID: templatesData?.selectedID || values._id,
      userEmailAddress: templatesData?.userEmailAddress || "",
      customerEmailAddress: [supplierEmail],
      emailTemplate: {
        templateSubject: subject.trim(),
        templateMessage: `<p>${escapeMessage(messageToSend)}</p>`,
      },
    });
  };

  return (
    <>
    <span ref={rootRef} hidden />
    <Modal open hideBackdrop disableEnforceFocus disableRestoreFocus
      onClose={() => {
        if (issueIsBusy) return;
        if (showEmailPreview) setShowEmailPreview(false);
        else if (showFullPreview) setShowFullPreview(false);
        else onClose?.();
      }}>
    <div
      style={
        workspaceBounds
          ? {
              ...workspaceBounds,
              height: `calc(100dvh - ${workspaceBounds.top}px)`,
            }
          : undefined
      }
      className={`rcti-communication-backdrop rcti-review-workspace${
        dark ? " dark" : ""
      }`}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !issueIsBusy) onClose?.();
      }}
    >
      <section
        ref={dialogRef}
        className="rcti-communication-dialog rcti-designed"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rcti-communication-title"
        aria-busy={issueIsBusy}
      >
        {!issueSucceeded && (
          <button
            type="button"
            className="rcti-workspace-back"
            disabled={issueIsBusy}
            onClick={() =>
              reviewStep === 2 && !isAdjustment && !Number(values.emailSent)
                ? setReviewStep(1)
                : onClose?.()
            }
          >
            ←{" "}
            {isAdjustment ? "Back to adjustment" : reviewStep === 1 ? "Back to draft" : "Back to eligibility review"}
          </button>
        )}
        <header className="rcti-communication-header">
          <div className="rcti-communication-heading-icon">
            {issueSucceeded ? (
              <LuCircleCheck />
            ) : issueIsBusy ? (
              <LuLoaderCircle className="is-spinning" />
            ) : (
              <LuMail />
            )}
          </div>
          <h2 className="type-h4" id="rcti-communication-title">
            {issueSucceeded
              ? isAdjustment
                ? "Adjustment issued"
                : "RCTI issued"
              : phase === "loading"
              ? "Preparing RCTI review…"
              : issueIsBusy
              ? "Issuing RCTI…"
              : isAdjustment
              ? "Review & issue adjustment"
              : Number(values.emailSent) > 0
              ? "Resend RCTI"
              : reviewStep === 1
              ? "Review RCTI eligibility"
              : "Preview & issue RCTI"}
          </h2>
          <button
            type="button"
            className="rcti-communication-close"
            aria-label="Close issue communications"
            onClick={onClose}
            disabled={issueIsBusy}
          >
            <LuX />
          </button>
        </header>

        {!issueSucceeded && (
          <>
            <p className="rcti-review-context">
              {values.rctiNumber || "Unsaved draft"} · {supplierName} ·{" "}
              {isAdjustment
                ? "Correction as a linked version"
                : "Check the evidence and supplier communication before issue."}
            </p>
            <RctiSteps
              steps={
                isAdjustment
                  ? [
                      ["Create adjustment", "Reason & revised values"],
                      ["Review & issue", "Comparison & supplier email"],
                    ]
                  : rctiSteps
              }
              current={isAdjustment ? 1 : reviewStep}
              enabledSteps={
                isAdjustment
                  ? [0, 1]
                  : reviewStep === 2 || eligibilityReady
                  ? [0, 1, 2]
                  : [0, 1]
              }
              onStepClick={(stepIndex) => {
                if (issueIsBusy) return;
                if (stepIndex === 0) {
                  onClose?.();
                  return;
                }
                if (isAdjustment) {
                  setReviewStep(2);
                  return;
                }
                setReviewStep(stepIndex === 2 ? 2 : 1);
              }}
            />
          </>
        )}
        {phase === "loading" ? (
          <PageSkeleton type="detail" variant="document" label="Preparing RCTI review…" />
        ) : issueSucceeded ? (
          <div className="rcti-communication-success">
            <div className="rcti-communication-feedback success">
              <LuCircleCheck />
              <div>
                <span className="rcti-type-emphasis">
                  Issued to {supplierAddress}
                </span>
                <span>The supplier communication was sent successfully.</span>
              </div>
            </div>
            <p className="rcti-communication-record">
              {values.rctiNumber} · {supplierName}
            </p>
            <div className="rcti-communication-badges">
              <span className="rcti-status status-payment-due">Unpaid</span>
              <span className="rcti-status status-sent">Delivery: Sent</span>
            </div>
            <div className="rcti-communication-money">
              <span>RCTI total</span>
              <span className="rcti-type-emphasis">
                {currencyCode} {amount}
              </span>
            </div>
            <div className="rcti-communication-money">
              <span>Payments made / Applied adjustments</span>
              <span className="rcti-type-emphasis">
                {currencyCode} {paid} / {adjustments}
              </span>
            </div>
            <div className="rcti-communication-money">
              <span>Outstanding payable</span>
              <span className="rcti-type-emphasis">
                {currencyCode} {outstanding}
              </span>
            </div>
            <p className="rcti-communication-due">Supply date {supplyDate}</p>
            <p className="rcti-communication-help">
              Issue did not record a payment. Returns to RCTI details with issue
              confirmation.
            </p>
            <button
              type="button"
              className="rcti-communication-view"
              onClick={onViewDetails}
            >
              View RCTI details
            </button>
          </div>
        ) : reviewStep === 1 ? (
          <RCTIEligibilityReview
            values={values}
            items={previewItems.length ? previewItems : values.itemsList || []}
            agreement={agreement}
            buyer={buyer}
            supplier={supplier}
            organisationName={organisationName}
            currencyCode={currencyCode}
            onEdit={onClose}
            onNavigateTo={onNavigateTo}
            onContinue={() => setReviewStep(2)}
            onReadinessChange={setEligibilityReady}
            adjustment={isAdjustment}
          />
        ) : (
          <>
            <div className="rcti-final-review-state">
              <span className="rcti-status status-draft">{isAdjustment ? "Adjustment preview" : "Draft / preview"}</span>
              <span>
                {values.rctiNumber || "Unsaved draft"} · {emailStatus} ·
                Delivery:{" "}
                {Number(values.emailSent) > 0 ? "Previously sent" : "not sent"}
              </span>
              <button type="button" className="rcti-detail-action" onClick={onClose} disabled={issueIsBusy}>
                {isAdjustment ? "Edit adjustment" : "Edit draft"}
              </button>
            </div>
            <div className="rcti-review-columns">
              <div className="rcti-review-document">
                <div className="rcti-review-document-bar">
                  Tax document preview <span>{emailStatus}</span>
                </div>
                {isAdjustment && adjustmentOriginal && (
                  <div className="rcti-adjustment-preview">
                    <RCTIAdjustmentComparison
                      original={adjustmentOriginal}
                      revised={values}
                      currencyCode={currencyCode}
                    />
                    <p>
                      <strong>Reason:</strong>{" "}
                      {values.adjustmentReason || "Not recorded"}
                    </p>
                  </div>
                )}
                {documentPreview}
              </div>
              <form id="rcti-issue-form" onSubmit={submit}>
                <section className="rcti-supplier-email-card">
                  <h2>Supplier email</h2>

                  {issueIsBusy && (
                    <div className="rcti-communication-feedback busy">
                      <LuLoaderCircle className="is-spinning" />
                      <div>
                        <span className="rcti-type-emphasis">
                          Please wait for confirmation
                        </span>
                        <span>
                          {isAdjustment ? "Issuing the revised version. Issued history is retained." : "The RCTI remains a draft until issue succeeds."}
                          {" "}Duplicate issue submission is disabled.
                        </span>
                      </div>
                    </div>
                  )}
                  {phase === "failed" && (
                    <div className="rcti-communication-feedback error">
                      <span className="rcti-type-emphasis">Issue failed</span>
                      <span>
                        {errorMessage ||
                          "The RCTI could not be issued. Please retry or review the document details."}
                      </span>
                    </div>
                  )}
                  <div className="rcti-communication-context">
                    <span>
                      {values.rctiNumber || "RCTI"} · {supplierName}
                    </span>
                    <span className="rcti-status status-draft">
                      {emailStatus}
                    </span>
                  </div>
                  <label className="rcti-communication-field">
                    To
                    <input
                      aria-label="To"
                      type="text"
                      value={supplierAddress}
                      title={supplierAddress}
                      readOnly
                      disabled={issueIsBusy}
                    />
                  </label>
                  <label className="rcti-communication-field">
                    From
                    <input
                      aria-label="From"
                      readOnly
                      title={`${organisationName} <${
                        templatesData?.userEmailAddress || "Email unavailable"
                      }>`}
                      value={`${organisationName} <${
                        templatesData?.userEmailAddress || "Email unavailable"
                      }>`}
                    />
                  </label>
                  <label className="rcti-communication-field">
                    Subject
                    <input
                      aria-label="Subject"
                      readOnly
                      value={rctiEmailPreview(subject, values, supplier, currencyCode)}
                      disabled={issueIsBusy}
                    />
                  </label>
                  <label className="rcti-communication-field">
                    Message
                    <textarea
                      aria-label="Message"
                      value={rctiEmailPreview(
                        message,
                        values,
                        supplier,
                        currencyCode
                      )}
                      disabled={issueIsBusy}
                      onChange={(event) => {
                        setMessage(
                          preserveTemplateTokens(event.target.value)
                        );
                        setValidationError("");
                      }}
                      rows={9}
                    />
                    <small className="rcti-email-message-help">
                      Edit the supplier message below. RCTI details in the
                      template are filled in when the document is issued.
                    </small>
                  </label>
                  <div className="rcti-communication-link">
                    <span className="rcti-type-emphasis">
                      Generated RCTI PDF attached
                    </span>
                    <span>
                      {values.rctiNumber} · Total {amount} {currencyCode} ·
                      Supply date {supplyDate}
                    </span>
                  </div>
                  <div className="rcti-communication-options">
                    <label>
                      <input type="checkbox" checked disabled readOnly />
                      Attach PDF
                    </label>
                  </div>
                  <p className="rcti-communication-disclaimer">
                    {isAdjustment ? "Issue confirms this revised version of the existing supplier payable." : "Issue confirms the saved RCTI and creates the supplier payable only after success."}
                  </p>
                  {validationError && (
                    <p className="rcti-communication-validation" role="alert">
                      {validationError}
                    </p>
                  )}
                </section>
                <section className="rcti-review-issue-info">
                  <h3>What happens when you issue</h3>
                  <p>
                    Eligibility is rechecked at issue. Successful issue locks
                    this version and {isAdjustment ? "updates the existing supplier payable to" : "creates the supplier payable of"}{" "}
                    {currencyCode} {amount}. Supplier email delivery is tracked
                    separately. Later corrections create a linked version of
                    this payable, preserving the issued history.
                  </p>
                  <label className="rcti-consent">
                    <input
                      type="checkbox"
                      checked={authorised}
                      disabled={issueIsBusy}
                      onChange={(event) => setAuthorised(event.target.checked)}
                    />
                    I confirm the document, buyer-set values, GST and supplier
                    email are correct and authorise issue on behalf of{" "}
                    {organisationName}.
                  </label>
                  <button
                    type="button"
                    className="primary"
                    onClick={() => setShowFullPreview(true)}
                    disabled={issueIsBusy}
                  >
                    Preview RCTI
                  </button>
                </section>
              </form>
            </div>
            <footer className="rcti-communication-actions rcti-final-review-footer">
              <button
                type="button"
                onClick={() =>
                  Number(values.emailSent) > 0 || isAdjustment
                    ? onClose?.()
                    : setReviewStep(1)
                }
                disabled={issueIsBusy}
              >
                {Number(values.emailSent) > 0 || isAdjustment
                  ? "Edit document"
                  : "Back to eligibility review"}
              </button>
              <button
                type="submit"
                form="rcti-issue-form"
                className="primary"
                disabled={issueIsBusy || !authorised}
              >
                {issueIsBusy && <LuLoaderCircle className="is-spinning" />}
                {issueIsBusy
                  ? "Issuing RCTI…"
                  : phase === "failed"
                  ? "Retry issue"
                  : isAdjustment
                  ? "Issue & email adjustment"
                  : Number(values.emailSent) > 0
                  ? "Resend RCTI"
                  : "Issue & email"}
              </button>
            </footer>
          </>
        )}
        {showFullPreview && !issueSucceeded && (
          <div
            className="rcti-invoice-preview-backdrop"
            style={
              workspaceBounds
                ? {
                    top: 0,
                    left: workspaceBounds.left,
                    width: workspaceBounds.width,
                    height: "100dvh",
                  }
                : undefined
            }
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setShowFullPreview(false);
            }}
          >
            <section
              className="rcti-invoice-preview-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="rcti-invoice-preview-title"
            >
              <header>
                <div>
                  <h2 id="rcti-invoice-preview-title">RCTI preview</h2>
                  <p>{values.rctiNumber || "New RCTI"} · Supplier copy</p>
                </div>
                <div className="rcti-invoice-preview-actions">
                  <button
                    type="button"
                    className="rcti-detail-action primary rcti-preview-issue-action"
                    onClick={() => {
                      setShowEmailPreview(true);
                    }}
                    disabled={issueIsBusy}
                  >
                    {isAdjustment ? "Issue & email adjustment" : Number(values.emailSent) > 0 ? "Resend RCTI" : "Issue & email"}
                  </button>
                  <button
                    type="button"
                    aria-label="Close invoice preview"
                    onClick={() => setShowFullPreview(false)}
                    disabled={issueIsBusy}
                  >
                    <LuX aria-hidden="true" />
                  </button>
                </div>
              </header>
              <div className="rcti-invoice-preview-document">{documentPreview}</div>
            </section>
          </div>
        )}
        <SendInvoiceModal
          open={showEmailPreview}
          documentLabel="RCTI"
          messageOnly
          invoiceNumber={values.rctiNumber}
          customerName={supplierName}
          customerEmail={supplierEmail}
          organisation={organisationName}
          finalAmount={Number(values.finalAmount || 0)}
          currency={currencyCode}
          invoiceStatus={isAdjustment ? "Adjustment preview" : Number(values.emailSent) > 0 ? "Issued" : "Draft / preview"}
          invoiceDate={values.rctiDate}
          templateData={{ ...templatesData, emailTemplate: { templateSubject: subject, templateMessage: rctiEmailPreview(message, values, supplier, currencyCode) } }}
          darkMode={dark}
          confirmationText={`I confirm the document, buyer-set values, GST and supplier email are correct and authorise issue on behalf of ${organisationName}.`}
          sendLabel={isAdjustment ? "Issue & email adjustment" : "Issue & email"}
          onClose={() => setShowEmailPreview(false)}
          onSend={(payload) => {
            setAuthorised(true);
            setShowEmailPreview(false);
            return onSubmit?.(payload);
          }}
        />
      </section>
    </div>
    </Modal>
    </>
  );
}
